import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FeatureFlagService, FeatureFlagStatusInterface } from '@galaxy/partner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  CommonField,
  CustomField,
  DeclinedReason,
  DeclinedReasonService,
  Field,
  SalesOrdersService,
} from '@vendasta/sales-orders';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest } from 'rxjs';
import { finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';

const AUTOMATIONS_ORDER_WORKFLOW = 'automations_order_workflow';

@Component({
  templateUrl: 'sales-order-decline-dialog.component.html',
  styleUrls: ['sales-order-decline-dialog.component.scss'],
})
export class SalesOrderDeclineDialogComponent implements OnInit, OnDestroy {
  @Input() orderId: string;
  @Input() businessId: string;
  @Input() title: string;
  @Input() details: string;

  @Input() commonFields?: CommonField[];
  @Input() customFields?: CustomField[];
  @Input() extraFields?: Field[];

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() error: EventEmitter<any> = new EventEmitter();

  form: UntypedFormGroup;

  formSubmitted$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  declinedReasons$$: ReplaySubject<DeclinedReason[]> = new ReplaySubject<DeclinedReason[]>(1);
  loadingDeclinedReasons$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  declinedReasonsApiError$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  submitDisabled$: Observable<boolean>;
  leaseOrderToAutomations$: Observable<boolean>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    public dialogRef: MatDialogRef<SalesOrderDeclineDialogComponent>,
    private salesOrdersService: SalesOrdersService,
    private declinedReasonsService: DeclinedReasonService,
    private snackbarService: SnackbarService,
    private featureFlagService: FeatureFlagService,
    private fb: UntypedFormBuilder,
    private router: Router,
    @Inject('partnerId$') private readonly partnerId$: Observable<string>,
  ) {}

  ngOnInit(): void {
    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.dialogRef.close();
    });

    this.leaseOrderToAutomations$ = this.salesOrdersService.get(this.orderId, this.businessId).pipe(
      switchMap((salesOrder) => {
        return this.featureFlagService.batchGetStatus(salesOrder.partnerId, salesOrder.marketId, [
          AUTOMATIONS_ORDER_WORKFLOW,
        ]);
      }),
      map((res: FeatureFlagStatusInterface) => res[AUTOMATIONS_ORDER_WORKFLOW]),
      take(1),
    );

    this.loadDeclinedReasons();
    this.submitDisabled$ = combineLatest([
      this.formSubmitted$$,
      this.loadingDeclinedReasons$$,
      this.declinedReasonsApiError$$,
    ]).pipe(map(([formSubmitted, loadingDeclinedReasons, error]) => formSubmitted || loadingDeclinedReasons || error));

    this.declinedReasons$$.pipe(takeUntil(this.destroyed$)).subscribe((declinedReasons) => {
      this.form = this.fb.group({
        reasonId: new UntypedFormControl('', declinedReasons.length > 0 ? [Validators.required] : null),
        message: new UntypedFormControl('', declinedReasons.length === 0 ? [Validators.required] : null),
      });
    });
  }

  get formReasonId(): UntypedFormControl {
    return this.form.controls.reasonId as UntypedFormControl;
  }

  get formMessage(): UntypedFormControl {
    return this.form.controls.message as UntypedFormControl;
  }

  loadDeclinedReasons(): void {
    this.loadingDeclinedReasons$$.next(true);
    this.declinedReasonsApiError$$.next(false);

    this.partnerId$
      .pipe(
        switchMap((partnerId) => this.declinedReasonsService.list(partnerId, null)),
        take(1),
        finalize(() => this.loadingDeclinedReasons$$.next(false)),
      )
      .subscribe(
        (declinedReasons) => this.declinedReasons$$.next(declinedReasons),
        () => this.declinedReasonsApiError$$.next(true),
      );
  }

  onDecline(): void {
    if (!this.form.valid) {
      return;
    }
    this.formSubmitted$$.next(true);
    this.salesOrdersService
      .decline(
        this.businessId,
        this.orderId,
        this.formMessage.value,
        this.formReasonId.value ? [this.formReasonId.value] : null,
      )
      .pipe(
        take(1),
        finalize(() => this.formSubmitted$$.next(false)),
      )
      .subscribe(
        (o) => this.dialogRef.close(o),
        (err) => {
          this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.DECLINE_REASON.ERROR');
          this.error.emit(err);
        },
      );
  }

  onLeaseBtnPressed(): void {
    if (!this.form.valid) {
      return;
    }
    this.formSubmitted$$.next(true);

    this.salesOrdersService
      .leaseToAutomations(
        this.businessId,
        this.orderId,
        this.customFields,
        this.commonFields,
        this.extraFields,
        'decline',
        this.formMessage.value,
        this.formReasonId.value ? [this.formReasonId.value] : null,
      )
      .pipe(
        take(1),
        finalize(() => this.formSubmitted$$.next(false)),
      )
      .subscribe(
        (o) => this.dialogRef.close(o),
        (err) => {
          this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.DECLINE_REASON.ERROR');
          this.error.emit(err);
        },
      );
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
